import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';
import '../styles/modal.css';

interface ModalProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly wide?: boolean;
}

/**
 * Dialogo modal em cima de `<dialog>` nativo.
 *
 * Usa o elemento nativo de proposito: ele ja da foco preso, `Esc`, backdrop inerte e
 * anuncio correto para leitor de tela. A versao "div com position fixed" sempre acaba
 * reimplementando isso pela metade — e a metade que falta e sempre a acessibilidade.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide = false,
}: ModalProps): ReactElement {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null || dialog.open) return;
    dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return createPortal(
    <dialog
      ref={ref}
      className={`modal${wide ? ' modal--wide' : ''}`}
      onCancel={(event) => {
        // `Esc` dispara `cancel`; sem o preventDefault o browser fecha o elemento sem
        // avisar o React, e o estado do pai fica achando que o modal ainda esta aberto.
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // O backdrop faz parte do proprio <dialog>: um clique nele tem o dialog como
        // alvo, enquanto qualquer clique no conteudo tem um filho como alvo.
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="modal__head">
        <div>
          <h2 className="modal__title">{title}</h2>
          {subtitle === undefined ? null : <p className="modal__subtitle">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="btn btn--icon"
          onClick={onClose}
          aria-label={t.common.close}
        >
          <Icon name="x" size={17} />
        </button>
      </div>

      <div className="modal__body">{children}</div>

      {footer === undefined ? null : <div className="modal__foot">{footer}</div>}
    </dialog>,
    document.body,
  );
}
